import { Component, type ErrorInfo, type ReactNode } from 'react'

// One thrown render used to be a blank white page with no way back — no message, no reload, nothing
// in the UI to tell you it wasn't just slow. A portal a coach opens ten minutes before a match
// should fail with a sentence and a button.
//
// A class component because that is still the only way to catch a render error in React.

interface Props {
  children: ReactNode
}
interface State {
  err: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { err: null }

  static getDerivedStateFromError(err: Error): State {
    return { err }
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    // No error-reporting service here (and adding one would be a third-party request the CSP
    // blocks), so the console is where this lives — enough for "send me the console" to work.
    console.error('Unhandled render error', err, info.componentStack)
  }

  render(): ReactNode {
    const { err } = this.state
    if (!err) return this.props.children
    return (
      <div className="mx-auto max-w-xl px-6 py-16">
        <div
          className="border p-6"
          style={{ borderRadius: 'var(--radius)', borderColor: 'var(--loss)', background: 'var(--surface)' }}
        >
          <div className="font-display text-sm uppercase tracking-wider text-loss">This page broke</div>
          <p className="mt-3 text-sm leading-relaxed text-text-dim">
            Something in the page threw an error, so it stopped rather than showing you numbers it
            couldn’t stand behind. The data is fine — this is a display fault.
          </p>
          <p className="mt-2 font-mono text-[11px] text-text-faint">{err.message}</p>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => {
                // Home, not reload: the route in the hash is often what broke.
                window.location.hash = '#/'
                window.location.reload()
              }}
              className="font-display px-4 py-2 text-xs font-semibold uppercase tracking-wider"
              style={{ borderRadius: 'var(--radius)', background: 'var(--accent)', color: 'var(--accent-ink)' }}
            >
              Back to matches
            </button>
            <button
              onClick={() => this.setState({ err: null })}
              className="px-4 py-2 text-xs text-text-dim hover:text-accent"
            >
              try again
            </button>
          </div>
        </div>
      </div>
    )
  }
}
